import { CalendarDate } from "@internationalized/date";
import type { RangeValue } from "@react-types/shared";
import { useEffect, useState } from "react";
import type { DateValue } from "react-aria";
import type { TimePeriod } from "../types";

// Custom hook for per-report date filtering with localStorage persistence
// If fixedStartDate and fixedEndDate are provided, the date range is fixed and not editable
export const useReportDateFilter = (
  reportId: string,
  componentType: "chart" | "table",
  fixedStartDate?: string,
  fixedEndDate?: string,
) => {
  const hasFixedDates = !!fixedStartDate && !!fixedEndDate;

  const [dateRangeMode, setDateRangeMode] = useState<"day" | "week" | "month">(
    (localStorage.getItem(`${componentType}-report-date-range-mode-${reportId}`) as "day" | "week" | "month") || "week",
  );
  const [dateRange, setDateRange] = useState<RangeValue<DateValue> | null>(null);

  // Helper to calculate days between two dates
  const calculateDaysBetween = (start: DateValue, end: DateValue): number => {
    const startDate = start.toDate("UTC");
    const endDate = end.toDate("UTC");
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Load fixed dates or cached date range from localStorage on mount
  useEffect(() => {
    // If fixed dates are provided, use them
    if (hasFixedDates) {
      try {
        const startDate = new Date(fixedStartDate);
        const endDate = new Date(fixedEndDate);
        const start = new CalendarDate(startDate.getFullYear(), startDate.getMonth() + 1, startDate.getDate());
        const end = new CalendarDate(endDate.getFullYear(), endDate.getMonth() + 1, endDate.getDate());
        setDateRange({ start, end });

        // Auto-select appropriate granularity based on date range duration
        const days = calculateDaysBetween(start, end);
        const savedMode = localStorage.getItem(`${componentType}-report-date-range-mode-${reportId}`) as
          | "day"
          | "week"
          | "month"
          | null;

        // Only override if no saved preference exists
        if (!savedMode) {
          if (days < 7) {
            setDateRangeMode("day");
          } else if (days < 60) {
            setDateRangeMode("week");
          } else {
            setDateRangeMode("month");
          }
        }
      } catch (error) {
        console.warn("Failed to parse fixed dates:", error);
      }
      return;
    }

    // Otherwise, load from localStorage
    try {
      const cachedDateRange = localStorage.getItem(`${componentType}-report-date-range-${reportId}`);

      if (cachedDateRange) {
        const parsed = JSON.parse(cachedDateRange);
        if (parsed.start && parsed.end) {
          const startDate = new CalendarDate(parsed.start.year, parsed.start.month, parsed.start.day);
          const endDate = new CalendarDate(parsed.end.year, parsed.end.month, parsed.end.day);
          setDateRange({ start: startDate, end: endDate });
        }
      }
    } catch (error) {
      console.warn("Failed to load cached date range:", error);
    }
  }, [reportId, componentType, fixedStartDate, fixedEndDate, hasFixedDates]);

  // Save date range to localStorage whenever it changes (only if not fixed)
  useEffect(() => {
    if (hasFixedDates) return; // Don't save to localStorage if dates are fixed

    const dateRangeCacheId = `${componentType}-report-date-range-${reportId}`;
    if (dateRange) {
      try {
        const serializedRange = {
          start: {
            year: dateRange.start.year,
            month: dateRange.start.month,
            day: dateRange.start.day,
          },
          end: {
            year: dateRange.end.year,
            month: dateRange.end.month,
            day: dateRange.end.day,
          },
        };
        localStorage.setItem(dateRangeCacheId, JSON.stringify(serializedRange));
      } catch (error) {
        console.warn("Failed to save date range to cache:", error);
      }
    } else {
      localStorage.removeItem(dateRangeCacheId);
    }
  }, [dateRange, reportId, componentType, hasFixedDates]);

  // Save date range mode to localStorage whenever it changes (only if not fixed)
  useEffect(() => {
    if (hasFixedDates) return; // Don't save to localStorage if dates are fixed

    localStorage.setItem(`${componentType}-report-date-range-mode-${reportId}`, dateRangeMode);
  }, [dateRangeMode, reportId, componentType, hasFixedDates]);

  // Map dateRangeMode to the API's expected timePeriod
  const timePeriod: TimePeriod = dateRangeMode === "day" ? "daily" : dateRangeMode === "month" ? "monthly" : "weekly";

  return {
    dateRangeMode,
    setDateRangeMode,
    dateRange,
    setDateRange,
    timePeriod,
    hasFixedDates,
  };
};
